import type { DocumentCheckIssue, StatementFile } from '../api';

export type StatusGroup = 'attention' | 'waiting' | 'ready';
// v9 `.chk` classes.
export type StatusTone = 'stop' | 'hard' | 'soft' | 'proc' | 'ok';

export interface StatementStatus {
	label: string;
	group: StatusGroup;
	tone: StatusTone;
	warning?: string;
}

export const STATUS_CHIPS: { id: StatusGroup | 'all'; label: string }[] = [
	{ id: 'all', label: 'All' },
	{ id: 'attention', label: 'Needs attention' },
	{ id: 'waiting', label: 'Waiting' },
	{ id: 'ready', label: 'Ready' },
];

const ISSUE_STATUS: Record<
	DocumentCheckIssue['code'],
	Pick<StatementStatus, 'label' | 'tone'>
> = {
	NOT_A_STATEMENT: { label: 'Not a statement', tone: 'stop' },
	MISSING_PAGES: { label: 'Needs pages', tone: 'hard' },
	UNREADABLE: { label: 'New file needed', tone: 'hard' },
	PARTLY_READABLE: { label: 'Review · page unclear', tone: 'soft' },
	OTHER: { label: 'Review', tone: 'soft' },
};

// Most severe first: the pill shows the one the user must act on.
const ISSUE_ORDER: DocumentCheckIssue['code'][] = [
	'NOT_A_STATEMENT',
	'MISSING_PAGES',
	'UNREADABLE',
	'PARTLY_READABLE',
	'OTHER',
];

export const statementStatus = (
	statement: Pick<StatementFile, 'current_status' | 'document_check'>,
): StatementStatus => {
	if (
		statement.current_status === 'uploaded' ||
		statement.current_status === 'processing'
	) {
		return { label: 'Processing', group: 'waiting', tone: 'proc' };
	}
	const check = statement.document_check;
	const issues = check?.status === 'flagged' ? check.issues : [];
	const issue =
		ISSUE_ORDER.map((code) => issues.find((i) => i.code === code)).find(
			Boolean,
		) ?? issues[0];
	if (issue) {
		return {
			...(ISSUE_STATUS[issue.code] ?? ISSUE_STATUS.OTHER),
			group: 'attention',
			warning: issue.ui_message,
		};
	}
	if (statement.current_status === 'error') {
		return { label: 'Failed', group: 'attention', tone: 'stop' };
	}
	return { label: 'Processed', group: 'ready', tone: 'ok' };
};

export const countByGroup = (statuses: StatementStatus[]) => {
	const counts = { all: statuses.length, attention: 0, waiting: 0, ready: 0 };
	statuses.forEach((s) => {
		counts[s.group] += 1;
	});
	return counts;
};

export const matchesChip = (
	status: StatementStatus,
	chip: StatusGroup | 'all',
): boolean => chip === 'all' || status.group === chip;
